import { useCallback, useEffect, useState } from 'react'
import Platform from '../../platforms/index.js'
import { getEvergreenMessaging } from '../../actions/contentActions.js'
import {
  EvergreenMessagingConfig,
  EvergreenMessagingApiResponse,
  UseEvergreenMessagingReturn,
  DEFAULT_BANNER_BACKGROUND_COLOR,
  DEFAULT_BANNER_TEXT_COLOR,
  EVERGREEN_BANNER_SLUG,
} from './types'
import type { Collection } from '../../reducers/types/reducerTypes'

/**
 * Validates if a string is a valid hex color code (#RRGGBB format)
 */
const isValidHexColor = (color: string): boolean =>
  /^#[0-9A-Fa-f]{6}$/.test(color)

/**
 * Inserts a banner marker collection after the Spotlight collection.
 * Returns the original array if no Spotlight is found.
 */
function insertBannerAfterSpotlight<T>(
  collections: Collection<T>[]
): Collection<T | Record<string, never>>[] {
  const spotlightIndex = collections.findIndex((c) => c.title === 'Spotlight')

  if (spotlightIndex === -1) {
    return collections
  }

  const bannerCollection: Collection<Record<string, never>> = {
    slug: EVERGREEN_BANNER_SLUG,
    title: 'Evergreen Urgent Banner',
    item_type: 'banner',
    content: [{}],
  }

  return [
    ...collections.slice(0, spotlightIndex + 1),
    bannerCollection,
    ...collections.slice(spotlightIndex + 1),
  ]
}

export default function useEvergreenMessagingFetch(): UseEvergreenMessagingReturn {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | undefined>()
  const [apiResponse, setApiResponse] = useState<
    EvergreenMessagingApiResponse | undefined
  >()

  useEffect(() => {
    getEvergreenMessaging()
      .then((response: EvergreenMessagingApiResponse) => {
        setApiResponse(response)
      })
      .catch((err) => {
        setError(err)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [])

  const getPlatformFlag = (
    deviceType: string
  ): keyof EvergreenMessagingApiResponse => {
    const platformFlagMap: Record<string, keyof EvergreenMessagingApiResponse> =
      {
        comcastx1: 'showMessageForComcast',
        lgtv: 'showMessageForLGTV',
        samsungtv: 'showMessageForSamsungTV',
        viziotv: 'showMessageForVizio',
      }

    return platformFlagMap[deviceType] || 'showMessageForSamsungTV'
  }

  const platformFlag = getPlatformFlag(Platform.deviceType)
  const shouldShowMessage = apiResponse
    ? Boolean(apiResponse[platformFlag])
    : false

  // Extract only the public config (without platform flags)
  // Apply default values for invalid hex colors and handle optional ctaImageUrl
  const messagingConfig: EvergreenMessagingConfig | undefined = apiResponse
    ? {
        bannerBackgroundColor: isValidHexColor(
          apiResponse.bannerBackgroundColor
        )
          ? apiResponse.bannerBackgroundColor
          : DEFAULT_BANNER_BACKGROUND_COLOR,
        bannerTextColor: isValidHexColor(apiResponse.bannerTextColor)
          ? apiResponse.bannerTextColor
          : DEFAULT_BANNER_TEXT_COLOR,
        campaign: apiResponse.campaign,
        ctaImageUrl: apiResponse.ctaImageUrl || undefined,
        ctaText: apiResponse.ctaText,
        descriptionText: apiResponse.descriptionText,
        displayUrl: apiResponse.displayUrl,
        displayUrlTextColor: apiResponse.displayUrlTextColor,
        headerText: apiResponse.headerText,
        qrCodeImageUrl: apiResponse.qrCodeImageUrl,
        urlLink: apiResponse.urlLink,
      }
    : undefined

  const getCollectionsWithBanner = useCallback(
    <T>(
      collections: Collection<T>[]
    ): Collection<T | Record<string, never>>[] =>
      shouldShowMessage ? insertBannerAfterSpotlight(collections) : collections,
    [shouldShowMessage]
  )

  return {
    shouldShowMessage,
    loading,
    error,
    messagingConfig,
    getCollectionsWithBanner,
  }
}
